import { IDestination } from '@stores/destination';
import isString from 'lodash/isString';
import { IRespData, LiveEvent } from './interface';

export const isNotWarehouseEvent = (event: LiveEvent) => !event.payload?.uploadID;

export const filterByTab = (events: LiveEvent[], selectedTab: string) => {
  if (selectedTab !== 'all') {
    return events.filter((event: LiveEvent) => event.jobState === selectedTab);
  }
  return events;
};

interface ISourceEvent {
  sourceId: string | string[];
}

export const filterBySources = <T extends ISourceEvent>(events: T[], sourceIds: string[]) => {
  if (sourceIds.length === 0) {
    return events;
  }
  return events.filter((event) => sourceIds.some((sourceId) => event.sourceId.includes(sourceId)));
};

const getDestination = (destination: IRespData) => {
  const jsonPayload = destination.payload?.body?.JSON;
  let eventName = isString(jsonPayload?.event) ? jsonPayload.event : '';
  const eventType = isString(jsonPayload?.type) ? jsonPayload.type : '';
  if (!eventName && !eventType) {
    eventName = 'Event';
  }
  return {
    eventName,
    eventType,
    sentAt: jsonPayload?.receivedAt,
  };
};

export const constructLiveEvents = (data: IRespData[]): LiveEvent[] =>
  data.map((event) => {
    let jobState: LiveEvent['jobState'] = 'failed';
    if (event.jobState === 'succeeded' || event.jobState === 'generated_staging_file') {
      jobState = 'success';
    } else if (event.jobState === 'filtered') {
      jobState = 'dropped';
    }
    return {
      ...event,
      jobState,
      ...(event.version !== 'v2' && getDestination(event)),
    };
  });

// 298 (filtered) and 299 (suppressed) never reach the destination: their body is router text.
const NOT_DELIVERED_CODES = ['298', '299'];

// The destination's reply to a delivered call, exactly as received (errorResponse.response).
// rudder-server keeps 2xx replies for destination types with saveDestinationResponse, and for
// the others while Live Events is open and Router.<destType>.liveEventsSuccessResponse is on.
// Failed and dropped rows are left to the error view: their text often comes from RudderStack.
export const getDestinationResponse = (
  event: LiveEvent,
): { statusCode: string; text: string } | undefined => {
  const { errorResponse, errorCode, jobState } = event;
  if (jobState !== 'success' || NOT_DELIVERED_CODES.includes(errorCode)) {
    return undefined;
  }
  if (!errorResponse || typeof errorResponse !== 'object') {
    return undefined;
  }
  const raw = (errorResponse as { response?: unknown }).response;
  if (!isString(raw) || raw.trim() === '') {
    return undefined;
  }
  return { statusCode: errorCode, text: raw };
};

export const getDropdownSources = (destination: IDestination) =>
  destination?.sources?.map((source) => ({
    value: source.id,
    label: source.name,
  })) || [];
